import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { CacheLookupPolicy, InteractionStatus } from '@azure/msal-browser';
import { from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { API_SCOPES } from './msal.config';
import { AuthService } from './auth.service';
import { APP_ROUTE_PATHS } from '../app.navigation';

export const authGuard: CanActivateFn = () => {
  if (environment.playwrightAuthBypass) {
    return true;
  }

  const msalService = inject(MsalService);
  const msalBroadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  // Wait for MSAL to be done processing before checking auth state
  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    switchMap(() => {
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        router.navigate(['/login']);
        return of(false);
      }

      const account = msalService.instance.getActiveAccount() ?? accounts[0];
      if (!msalService.instance.getActiveAccount()) {
        msalService.instance.setActiveAccount(account);
      }

      return from(
        msalService.instance.acquireTokenSilent({
          scopes: API_SCOPES,
          account,
          cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
        })
      ).pipe(
        map(() => true),
        catchError(() => {
          router.navigate(['/login'], {
            queryParams: { sessionExpired: 'true' },
          });
          return of(false);
        })
      );
    })
  );
};

export const loginGuard: CanActivateFn = (route) => {
  if (environment.playwrightAuthBypass) {
    const router = inject(Router);
    router.navigate(['/home']);
    return false;
  }

  const msalService = inject(MsalService);
  const msalBroadcastService = inject(MsalBroadcastService);
  const router = inject(Router);
  const allowSessionExpiredLogin = route.queryParamMap.get('sessionExpired') === 'true';

  // Wait for MSAL to be done processing before checking auth state
  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    map(() => {
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length > 0 && !allowSessionExpiredLogin) {
        router.navigate(['/home']);
        return false;
      }
      return true;
    })
  );
};

export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (environment.playwrightAuthBypass) {
    return true;
  }

  return authService.resolveAdminStatus().pipe(
    map((isAdmin) => isAdmin || router.createUrlTree([APP_ROUTE_PATHS.home])),
    catchError(() => of(router.createUrlTree([APP_ROUTE_PATHS.home])))
  );
};
